import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import { dateLabel } from '@mds/shared/lib/format';
import { checkSurveyAnswers, isChoiceQuestion, surveyMaxLength } from '@mds/shared/lib/survey';
import {
  SURVEY_ISSUE_CODES,
  type SurveyAnswer,
  type SurveyAnswers,
  type SurveyChannel,
  type SurveyDefinition,
  type SurveyIssueCode,
  type SurveyLanguage,
  type SurveyQuestion,
  type SurveyText,
} from '@mds/shared/types/survey';

/**
 * Reading and filling in a survey in the admin: labels in the language being read, the typed
 * follow-ups that explain a pick, and the form's checks with their messages in that language.
 * The rules themselves are the API's, from `@mds/shared/lib/survey`.
 */

export const LANGUAGE_LABELS: Record<SurveyLanguage, string> = { en: 'English', ar: 'العربية' };

export const CHANNEL_LABELS: Record<SurveyChannel, Record<SurveyLanguage, string>> = {
  paper: { en: 'Paper form', ar: 'استمارة ورقية' },
  'in-person': { en: 'In person', ar: 'مقابلة شخصية' },
  phone: { en: 'By phone', ar: 'عبر الهاتف' },
};

/** Text in the language being read, or in whichever language the definition does have it in. */
export function textIn(text: SurveyText | undefined, language: SurveyLanguage): string {
  if (!text) return '';
  return text[language] || Object.values(text).find(Boolean) || '';
}

/** Western digits for the stored codes, Arabic-Indic ones to read in Arabic. */
export const count = (n: number, language: SurveyLanguage) =>
  n.toLocaleString(language === 'ar' ? 'ar-LB' : 'en-GB');

/** How a list of picks is joined in each language. */
const LIST_SEPARATOR: Record<SurveyLanguage, string> = { en: ', ', ar: '، ' };

export function optionLabel(question: SurveyQuestion, code: string, language: SurveyLanguage) {
  const option = question.options?.find((o) => o.value === code);
  return (option && textIn(option.label, language)) || code;
}

/** A question as the reader sees it: the number beside it, which a group lends its first one. */
export interface ReadQuestion {
  question: SurveyQuestion;
  number?: string;
}

/** Every question in the order it is asked, each with the number it is read under. */
export function readQuestions(definition: SurveyDefinition): ReadQuestion[] {
  const read: ReadQuestion[] = [];
  for (const section of definition.sections) {
    for (const item of section.items) {
      if (item.kind === 'question') {
        read.push({ question: item, number: item.number });
      } else {
        item.questions.forEach((question, i) =>
          read.push({ question, number: question.number ?? (i === 0 ? item.number : undefined) }),
        );
      }
    }
  }
  return read;
}

/**
 * The typed questions that explain a pick — "Other — what?" under a list's `other` — keyed by
 * `question:code`. They are drawn under the question they explain and read as part of its answer.
 */
export function followUpsOf(definition: SurveyDefinition): Map<string, SurveyQuestion> {
  const choices = new Set(
    readQuestions(definition)
      .map(({ question }) => question)
      .filter(isChoiceQuestion)
      .map((question) => question.key),
  );
  const followUps = new Map<string, SurveyQuestion>();
  for (const { question } of readQuestions(definition)) {
    if (isChoiceQuestion(question) || !question.showIf) continue;
    if (!choices.has(question.showIf.question)) continue;
    for (const code of question.showIf.anyOf) {
      followUps.set(`${question.showIf.question}:${code}`, question);
    }
  }
  return followUps;
}

/** The keys of every follow-up, so a reader lists them inside their question rather than apart. */
export const followUpKeys = (followUps: Map<string, SurveyQuestion>) =>
  new Set([...followUps.values()].map((question) => question.key));

/** An answer by key, never one inherited from `Object.prototype`. */
export function answerTo(answers: SurveyAnswers, key: string): SurveyAnswer | undefined {
  return Object.hasOwn(answers, key) ? answers[key] : undefined;
}

/**
 * One answer as a line of text. A pick with a typed follow-up reads "Other: what they wrote",
 * or with `followUpOnly`, just what they wrote — for a table cell, where the label adds nothing.
 */
export function answerText(
  question: SurveyQuestion,
  answers: SurveyAnswers,
  followUps: Map<string, SurveyQuestion>,
  language: SurveyLanguage,
  { followUpOnly = false }: { followUpOnly?: boolean } = {},
): string {
  const answer = answerTo(answers, question.key);
  if (answer === undefined || answer === '' || (Array.isArray(answer) && answer.length === 0)) {
    return '';
  }
  if (!isChoiceQuestion(question)) {
    return question.type === 'date' && typeof answer === 'string'
      ? dateLabel(answer)
      : String(answer);
  }
  const codes = Array.isArray(answer) ? answer : [answer];
  return codes
    .map((code) => {
      const label = optionLabel(question, code, language);
      const followUp = followUps.get(`${question.key}:${code}`);
      const typed = followUp ? answerTo(answers, followUp.key) : undefined;
      if (typeof typed !== 'string' || !typed) return label;
      return followUpOnly ? typed : `${label}: ${typed}`;
    })
    .join(LIST_SEPARATOR[language]);
}

/* -------------------------------------------------------------- the form --- */

/** What the form holds while it is filled in. Answers are kept by question key, blanks and all. */
export interface ResponseDraft {
  collectedOn: string;
  channel: SurveyChannel | '';
  answers: SurveyAnswers;
}

const MESSAGES = {
  en: {
    pick: 'Pick an answer.',
    pickSome: 'Tick at least one box.',
    enter: 'Enter an answer.',
    phone: 'Enter a phone or WhatsApp number.',
    date: 'Enter the date it was filled in.',
    invalidDate: 'Enter a real date.',
    option: 'Pick one of the options.',
    type: 'That answer is not in the right form.',
    duplicate: 'An option is ticked twice.',
    exclusive: (label: string) => `"${label}" can only be ticked on its own.`,
    tooMany: (n: string) => `Tick ${n} at most.`,
    tooLong: (n: string) => `Keep it to ${n} characters.`,
    unknown: 'This question is no longer in the survey.',
  },
  ar: {
    pick: 'اختاروا إجابة.',
    pickSome: 'اختاروا خياراً واحداً على الأقل.',
    enter: 'أدخلوا إجابة.',
    phone: 'أدخلوا رقم الهاتف أو واتساب.',
    date: 'أدخلوا تاريخ تعبئة الاستبيان.',
    invalidDate: 'أدخلوا تاريخاً صحيحاً.',
    option: 'اختاروا أحد الخيارات.',
    type: 'صيغة الإجابة غير صحيحة.',
    duplicate: 'خيار محدّد مرتين.',
    exclusive: (label: string) => `«${label}» لا يُختار مع غيره.`,
    tooMany: (n: string) => `اختاروا ${n} كحد أقصى.`,
    tooLong: (n: string) => `لا تتجاوزوا ${n} حرفاً.`,
    unknown: 'لم يعد هذا السؤال في الاستبيان.',
  },
} as const;

const ISSUE_CODES: ReadonlySet<string> = new Set(SURVEY_ISSUE_CODES);

/** The message for a refused answer, in the language being read. */
export function issueMessage(
  definition: SurveyDefinition,
  key: string,
  code: SurveyIssueCode,
  answers: SurveyAnswers,
  language: SurveyLanguage,
): string {
  const m = MESSAGES[language];
  const question = readQuestions(definition).find((r) => r.question.key === key)?.question;
  if (!question) return m.unknown;
  switch (code) {
    case 'required':
      if (question.type === 'single') return m.pick;
      if (question.type === 'multiple') return m.pickSome;
      return question.type === 'phone' ? m.phone : m.enter;
    case 'invalid_option':
      return m.option;
    case 'invalid_type':
      return m.type;
    case 'duplicate_option':
      return m.duplicate;
    case 'exclusive': {
      const picked = answerTo(answers, key);
      const lone = question.options?.find(
        (o) => o.exclusive && Array.isArray(picked) && picked.includes(o.value),
      );
      return m.exclusive(lone ? textIn(lone.label, language) : '');
    }
    case 'too_many':
      return m.tooMany(count(question.maxSelections ?? 0, language));
    case 'too_long':
      return m.tooLong(count(surveyMaxLength(question), language));
    case 'invalid_phone':
      return m.phone;
    case 'invalid_date':
      return m.invalidDate;
    case 'unknown_question':
      return m.unknown;
  }
}

/**
 * The form's schema for `useValidation`: the response details, then every answer through the
 * same check the API runs. Errors land on `answers.<key>`, the path the API names in `details`.
 */
export function responseSchema(definition: SurveyDefinition, language: SurveyLanguage) {
  const m = MESSAGES[language];
  return z.object({
    collectedOn: z.string().min(1, m.date),
    channel: z.string().min(1, m.pick),
    answers: z.custom<SurveyAnswers>().superRefine((answers, ctx) => {
      for (const issue of checkSurveyAnswers(definition, answers).issues) {
        const key = issue.path[1] ?? '';
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: issueMessage(definition, key, issue.code, answers, language),
        });
      }
    }),
  });
}

/**
 * The field errors in a refused save, by dotted path, in the language being read. Empty when the
 * refusal was not about the answers, so the caller shows its message instead.
 */
export function serverErrors(
  error: unknown,
  definition: SurveyDefinition,
  answers: SurveyAnswers,
  language: SurveyLanguage,
): Record<string, string> {
  if (!(error instanceof ApiError) || !Array.isArray(error.body?.details)) return {};
  const m = MESSAGES[language];
  const errors: Record<string, string> = {};
  for (const detail of error.body.details as {
    path?: unknown;
    code?: unknown;
    message?: unknown;
  }[]) {
    if (!Array.isArray(detail.path) || detail.path.length === 0) continue;
    const path = detail.path.map(String);
    const at = path.join('.');
    if (at in errors) continue;
    if (path[0] === 'answers' && path[1] && ISSUE_CODES.has(String(detail.code))) {
      errors[at] = issueMessage(
        definition,
        path[1],
        detail.code as SurveyIssueCode,
        answers,
        language,
      );
    } else if (path[0] === 'collectedOn') {
      errors[at] = m.date;
    } else if (path[0] === 'channel') {
      errors[at] = m.pick;
    } else if (typeof detail.message === 'string') {
      errors[at] = detail.message;
    }
  }
  return errors;
}
